import { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = 'https://weridesudan.com/api/admin';

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

export default function AIAssistant({ token }: { token: string }) {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', text: 'Hello! I am your WeRide Sudan AI assistant. Ask me anything about your rides, drivers, or riders.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/ai`, { message: msg }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong.' }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleVoice = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser. Please use Chrome.');
      return;
    }

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setListening(false);
      sendMessage(transcript);
    };

    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  };

  return (
    <div style={{ padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h2>🤖 AI Assistant</h2>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f5f5f5', borderRadius: 8, marginBottom: 16, minHeight: 300 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
            <div style={{
              maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
              background: m.role === 'user' ? '#1a73e8' : '#fff',
              color: m.role === 'user' ? '#fff' : '#333',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div style={{ textAlign: 'center', color: '#999' }}>Thinking...</div>}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder="Ask about your rides, drivers, revenue..."
          style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
        />

        {/* Mic button */}
        <button
          onClick={toggleVoice}
          title={listening ? 'Stop recording' : 'Speak'}
          style={{
            padding: '10px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 18,
            background: listening ? '#ef4444' : '#e8f0fe',
            color: listening ? '#fff' : '#1a73e8',
            transition: 'all 0.2s',
            animation: listening ? 'pulse 1s infinite' : 'none',
          }}
        >
          🎤
        </button>

        {/* Send button */}
        <button
          onClick={() => sendMessage()}
          disabled={loading}
          style={{ padding: '10px 20px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
        >
          Send
        </button>
      </div>

      {listening && (
        <div style={{ textAlign: 'center', color: '#ef4444', marginTop: 8, fontSize: 13 }}>
          🔴 Listening... speak now
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}